import CloseSmallIcon from 'assets/icons/close-small-icon.svg';
import SendIcon from 'assets/ver2/icons/header/remittance-icon.svg';
import {
  redirectToTransactionDetails,
  updateNotifyToReaded,
} from 'components/Profile/func';
import Transactions from 'components/Transactions';
import FilterTransaction from 'components/Transactions/components/FilterTransaction';
import PaginationLazy from 'components/Transactions/components/Pagination/mobile';
import SearchTransaction from 'components/Transactions/components/SearchTransaction';
import {
  convertFilteredKeyFixed,
  fetchTransactionsFiltered,
  getBodyPayloadFiltered,
} from 'components/Transactions/func';
import TransactionsMobile from 'components/Transactions/mobile';
import ButtonCommon from 'components/common/ButtonCommon';
import CollapsibleSideMenu from 'components/common/CollapsibleSideMenu';
import OuterBodyContent from 'components/common/CollapsibleSideMenu/OuterBodyContent';
import OuterPathContent, {
  SearchFilterSection,
  Title,
} from 'components/common/CollapsibleSideMenu/OuterPathContent';
import MobileTabMenu from 'components/common/MobileTabMenu';
import NavbarFooter from 'components/common/NavbarFooter/ver3';
import SizeBox from 'components/common/SizeBox';
import {
  PAGE_DEFAULT,
  SEARCH_BY_DEFAULT,
  TRANSACTION_ADD_PREFIX,
  TRANSACTION_STATUS_TRANSLATE_REFERENCE,
  TRANSACTION_TABLE_SORT_ORDER_INITIAL,
} from 'constants';
import { datePicker, format, searchTextValid } from 'helpers';
import Cookies from 'helpers/cookies';
import { MAKE_PAYMENT_KEY } from 'hooks/form-add-transaction/useAddTransaction';
import useAuth from 'hooks/useAuth';
import useBodyContentStatus, { Pill } from 'hooks/useBodyContentStatus';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useFormAddTransactionStore from 'hooks/form-add-transaction/useFormAddTransactionStore';
import useLang from 'hooks/useLang';
import useQuery from 'hooks/useQuery';
import useStore from 'hooks/useStore';
import { isArray, isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { arrayUtils } from 'utils/func';

const TransactionsPage = () => {
  const { t } = useLang();
  const { state } = useStore();
  const { currentUser, notifications } = state;
  const { token } = useAuth();
  const navigate = useNavigate();
  const { resetSendMoneyFlow } = useFormAddTransactionStore();
  const { isMobile, isTablet, isDesktop } = useDetectDevice();
  const query = useQuery();
  const tid = query.get('tid');
  const mid = query.get('mid');
  const { refreshTransactionsList } = state;

  const [transactions, setTransactions] = useState(null);
  const [currentPage, setCurrentPage] = useState(PAGE_DEFAULT);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [sortOrder, setSortOrder] = useState(
    TRANSACTION_TABLE_SORT_ORDER_INITIAL
  );
  const [applyFiltered, setApplyFiltered] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [currentSearchBy, setCurrentSearchBy] = useState(SEARCH_BY_DEFAULT);
  const [isFetching, setFetching] = useState(false);
  const [isSearched, setSearched] = useState(false);
  // Set by an invalid submit; cleared on the next edit or a valid submit. The
  // message shows only while the text is also invalid (isSearchInvalid).
  const [isSearchSubmittedInvalid, setSearchSubmittedInvalid] =
    useState(false);
  const isSearchInvalid =
    isSearchSubmittedInvalid && !searchTextValid(searchValue);

  const {
    isShowSearchInput,
    isShowFilterButton,
    isShowActionButton,
    isShowPagingButton,
    isShowPillFiltered,
  } = useBodyContentStatus({
    isFetching,
    list: transactions,
    sorts: sortOrder,
    filters: applyFiltered,
    search: searchValue,
  });

  useEffect(() => {
    if (notifications && Array.isArray(notifications) && tid && mid) {
      const notiData = notifications?.find(
        (noti) => noti?.data?.tranId === tid && noti?.uuid === mid
      );

      if (notiData) {
        if (!notiData?.read) {
          updateNotifyToReaded(currentUser, notiData);
        }

        redirectToTransactionDetails(token, notiData, navigate);
      }
    }
  }, [notifications, tid, mid]);

  const isFetchTransactionsInitial =
    token &&
    !isArray(transactions) &&
    isEmpty(transactions) &&
    !searchValue &&
    isEmpty(applyFiltered) &&
    Object.values(sortOrder).every((value) => !value) &&
    refreshTransactionsList;

  useEffect(() => {
    if (isFetchTransactionsInitial) {
      setFetching(true);
      setTransactions(null);
      fetchTransactionsFiltered({
        token,
        page: PAGE_DEFAULT,
        transactions: [],
        setTransactions,
        setCurrentPage,
        setTotalPages,
        setTotalElements,
        setFetching,
      });
    }
  }, [isFetchTransactionsInitial]);

  const removeFilterByArray = (key, arr, obj, prop) => {
    const arrayFilter = arrayUtils.removeItemInArrayObject(
      arr,
      obj[prop],
      prop
    );

    if (arrayUtils.isArrayEmpty(arrayFilter)) {
      delete applyFiltered[key];

      return applyFiltered;
    }

    const newApplyFiltered = {
      ...applyFiltered,
      [key]: arrayFilter,
    };
    return newApplyFiltered;
  };
  const removeFilterByDate = () => {
    delete applyFiltered['dateFromSelected'];

    delete applyFiltered['dateToSelected'];

    return applyFiltered;
  };
  const removeFilterByAmount = () => {
    delete applyFiltered['amountFrom'];

    delete applyFiltered['amountTo'];

    return applyFiltered;
  };
  const removeTypeFilteredReference = {
    countriesSelected: (key, array, country) => {
      return removeFilterByArray(key, array, country, 'code');
    },
    statusSelected: (key, array, status) => {
      return removeFilterByArray(key, array, status, 'code');
    },
    dateFromSelected: () => {
      return removeFilterByDate();
    },
    dateToSelected: () => {
      return removeFilterByDate();
    },
    amountFrom: () => {
      return removeFilterByAmount();
    },
    amountTo: () => {
      return removeFilterByAmount();
    },
  };
  const handleRemoveFiltered = (typeFilter, value) => {
    if (!searchTextValid(searchValue)) return;

    setFetching(true);

    const newApplyFiltered = removeTypeFilteredReference[typeFilter](
      typeFilter,
      applyFiltered[typeFilter],
      value
    );
    setApplyFiltered(newApplyFiltered);

    let bodyPayload = getBodyPayloadFiltered(newApplyFiltered);

    bodyPayload = {
      ...bodyPayload,
      [currentSearchBy]: searchValue,
    };

    setTransactions(null);
    fetchTransactionsFiltered({
      token,
      body: bodyPayload,
      page: PAGE_DEFAULT,
      setTransactions,
      setCurrentPage,
      setTotalPages,
      setTotalElements,
      setFetching,
    });
  };

  const renderFilterByArray = (key, arr, isTranslate) => {
    return arr.map((item, index) => (
      <Pill key={`${key}-${index}`}>
        {isTranslate
          ? t(TRANSACTION_STATUS_TRANSLATE_REFERENCE[item.name])
          : item.name}{' '}
        <img
          src={CloseSmallIcon}
          width={12}
          height={12}
          alt=""
          onClick={() => handleRemoveFiltered(key, item)}
        />
      </Pill>
    ));
  };
  const getDateStr = (key) =>
    datePicker.dateUTCToDateStr(new Date(applyFiltered[key]));
  const renderFilterByDate = (key) => {
    const keyDateFrom = 'dateFromSelected';
    const keyDateTo = 'dateToSelected';

    if (applyFiltered[keyDateFrom] && applyFiltered[keyDateTo]) {
      if (key === keyDateFrom) {
        return (
          <Pill key={`From ${keyDateFrom} To ${keyDateTo}`}>
            {`${getDateStr(keyDateFrom)} - ${getDateStr(keyDateTo)}`}{' '}
            <img
              src={CloseSmallIcon}
              width={12}
              height={12}
              alt=""
              onClick={() => handleRemoveFiltered(keyDateFrom)}
            />
          </Pill>
        );
      }

      return null;
    }

    if (applyFiltered[keyDateFrom] && !applyFiltered[keyDateTo]) {
      return (
        <Pill key={`${keyDateFrom}`}>
          {`From ${getDateStr(keyDateFrom)} To -`}{' '}
          <img
            src={CloseSmallIcon}
            width={12}
            height={12}
            alt=""
            onClick={() => handleRemoveFiltered(keyDateFrom)}
          />
        </Pill>
      );
    }

    if (!applyFiltered[keyDateFrom] && applyFiltered[keyDateTo]) {
      return (
        <Pill key={`${keyDateTo}`}>
          {`From - To ${getDateStr(keyDateTo)}`}{' '}
          <img
            src={CloseSmallIcon}
            width={12}
            height={12}
            alt=""
            onClick={() => handleRemoveFiltered(keyDateTo)}
          />
        </Pill>
      );
    }

    return null;
  };
  const renderFilterByAmount = (key) => {
    const keyAmountFrom = 'amountFrom';
    const keyAmountTo = 'amountTo';

    if (applyFiltered[keyAmountFrom] && !applyFiltered[keyAmountTo]) {
      return (
        <Pill key={`${keyAmountFrom}`}>
          {`AUD: Minimum ${format.toAmountStr(applyFiltered[keyAmountFrom])}`}{' '}
          <img
            src={CloseSmallIcon}
            width={12}
            height={12}
            alt=""
            onClick={() => handleRemoveFiltered(keyAmountFrom)}
          />
        </Pill>
      );
    }

    if (!applyFiltered[keyAmountFrom] && applyFiltered[keyAmountTo]) {
      return (
        <Pill key={`${keyAmountTo}`}>
          {`AUD: Maximum ${format.toAmountStr(applyFiltered[keyAmountTo])}`}{' '}
          <img
            src={CloseSmallIcon}
            width={12}
            height={12}
            alt=""
            onClick={() => handleRemoveFiltered(keyAmountTo)}
          />
        </Pill>
      );
    }

    if (key === keyAmountTo) return null;

    return (
      <Pill key={`${keyAmountFrom}-${keyAmountTo}`}>
        {`AUD: ${format.toAmountStr(
          applyFiltered[keyAmountFrom]
        )} - ${format.toAmountStr(applyFiltered[keyAmountTo])}`}{' '}
        <img
          src={CloseSmallIcon}
          width={12}
          height={12}
          alt=""
          onClick={() => handleRemoveFiltered(keyAmountFrom)}
        />
      </Pill>
    );
  };
  const renderTypeFilteredReference = {
    countriesSelected: (key, array) => {
      return renderFilterByArray(key, array);
    },
    statusSelected: (key, array) => {
      return renderFilterByArray(key, array, true);
    },
    dateFromSelected: (key) => {
      return renderFilterByDate(key);
    },
    dateToSelected: (key) => {
      return renderFilterByDate(key);
    },
    amountFrom: (key) => {
      return renderFilterByAmount(key);
    },
    amountTo: (key) => {
      return renderFilterByAmount(key);
    },
  };

  const renderSearchInput = () => {
    if (!isShowSearchInput) return null;
    return (
      <SearchTransaction
        applyFiltered={applyFiltered}
        currentSearchBy={currentSearchBy}
        setCurrentSearchBy={setCurrentSearchBy}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        transactions={transactions}
        setTransactions={setTransactions}
        setTotalPages={setTotalPages}
        setCurrentPage={setCurrentPage}
        setSortOrder={setSortOrder}
        setTotalElements={setTotalElements}
        setFetching={setFetching}
        setSearched={setSearched}
        isSearchInvalid={isSearchInvalid}
        setSearchSubmittedInvalid={setSearchSubmittedInvalid}
      />
    );
  };
  const renderFilterButton = () => {
    if (!isShowFilterButton) return null;
    return (
      <FilterTransaction
        applyFiltered={applyFiltered}
        currentSearchBy={currentSearchBy}
        searchValue={searchValue}
        isSearchInvalid={isSearchInvalid}
        setApplyFiltered={setApplyFiltered}
        setTransactions={setTransactions}
        setTotalPages={setTotalPages}
        setCurrentPage={setCurrentPage}
        setSortOrder={setSortOrder}
        setTotalElements={setTotalElements}
        setFetching={setFetching}
      />
    );
  };
  const renderActionButton = () => {
    if (!isShowActionButton) return null;
    if (!isDesktop) return null;
    return (
      <ButtonCommon
        value={t('button_send_money')}
        color="var(--c-primary)"
        background="var(--bg-primary)"
        isFill={true}
        styles={{
          paddingInline: 0,
        }}
        buttonIconStyles={{
          float: 'right',
        }}
        iconSrc={SendIcon}
        onClick={() => {
          Cookies.remove(MAKE_PAYMENT_KEY);
          resetSendMoneyFlow();
          navigate(`${TRANSACTION_ADD_PREFIX}`);
        }}
      />
    );
  };
  const renderPagingButton = () => {
    if (!isShowPagingButton) return null;
    if (!isDesktop)
      return (
        <PaginationLazy
          applyFiltered={applyFiltered}
          searchValue={searchValue}
          currentSearchBy={currentSearchBy}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          transactions={transactions}
          setTransactions={setTransactions}
          isSearched={isSearched}
        />
      );
    return null;
  };
  const renderFilteredPills = () => {
    if (!isShowPillFiltered) return null;
    return (
      <SearchFilterSection>
        {Object.keys(convertFilteredKeyFixed(applyFiltered)).map((key) => {
          return renderTypeFilteredReference[key](key, applyFiltered[key]);
        })}
      </SearchFilterSection>
    );
  };
  const renderSearchFilterActionPagingSection = () => (
    <>
      {renderSearchInput()}
      {renderFilterButton()}
      {renderActionButton()}
      {renderPagingButton()}
      {renderFilteredPills()}
    </>
  );

  const renderTransactionsSection = () => {
    if (isFetchTransactionsInitial) return;
    if (!isDesktop)
      return (
        <TransactionsMobile
          transactions={transactions}
          setTransactions={setTransactions}
          searchValue={searchValue}
          applyFiltered={applyFiltered}
          currentSearchBy={currentSearchBy}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          sortOrder={sortOrder}
          setTotalElements={setTotalElements}
          setTotalPages={setTotalPages}
          isFetching={isFetching}
          setFetching={setFetching}
          isSearched={isSearched}
          setSearched={setSearched}
        />
      );
    return (
      <Transactions
        currentPage={currentPage}
        totalPages={totalPages}
        transactions={transactions}
        setTransactions={setTransactions}
        applyFiltered={applyFiltered}
        searchValue={searchValue}
        currentSearchBy={currentSearchBy}
        setTotalPages={setTotalPages}
        setCurrentPage={setCurrentPage}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        totalElements={totalElements}
        setTotalElements={setTotalElements}
        isFetching={isFetching}
        setFetching={setFetching}
        isSearched={isSearched}
        setSearched={setSearched}
      />
    );
  };

  const getOuterPathContentStyle = () => {
    if (!isDesktop) return {};
    return {
      minHeight: '80vh',
    };
  };

  if (isMobile || isTablet) {
    return (
      <OuterBodyContent>
        <MobileTabMenu />
        {renderSearchFilterActionPagingSection()}
        {renderTransactionsSection()}
        <NavbarFooter />
      </OuterBodyContent>
    );
  }
  return (
    <CollapsibleSideMenu>
      <OuterBodyContent>
        <OuterPathContent style={getOuterPathContentStyle()}>
          <Title>{t('transactions_title')}</Title>
          <SizeBox height={16} />
          {renderSearchFilterActionPagingSection()}
          {renderTransactionsSection()}
        </OuterPathContent>
      </OuterBodyContent>
    </CollapsibleSideMenu>
  );
};

export default TransactionsPage;
